import { DatePipe, DecimalPipe, NgStyle } from "@angular/common";
import {
    Component,
    NgZone,
    OnDestroy,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { RouterLink } from "@angular/router";
import { ArbetsorderInfoIconComponent } from "@components/arbetsorder-info-icon/arbetsorder-info-icon.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { NgbPopover } from "@ng-bootstrap/ng-bootstrap";
import { isNil } from "lodash";
import moment from "moment";
import { BehaviorSubject, Subject } from "rxjs";
import { filter, map, takeUntil } from "rxjs/operators";
import { Anskaffningsformer, ValsSynkroniseringsstatusTyp, VirkesorderStatus } from "~app/common/models/constants";
import { CalendarService } from "~app/common/services/calendar.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { VirkesorderStatusChangedEventMessage } from "~app/features/planera/events/VirkesorderStatusChangedEventMessage";
import { ArbetsorderVirkesorderService } from "~app/features/planera/services/arbetsorder-virkesorder.service";
import { ArbetsorderEventStore } from "~app/features/shared/services/arbetsorderEventStore.service";
import { ArbetsorderStatusIcons, IArbetsorderStatusIcons } from "../../arbetsorder-status-ikoner/arbetsorder-status-ikoner-models";
import { ArbetsorderStatusIkonerComponent } from "../../arbetsorder-status-ikoner/arbetsorder-status-ikoner.component";
import { IMaskinLagSchedulerEvent } from "../../models/IMaskinLagSchedulerEvent";
import { SchemaPopupInfoDTO } from "../../models/SchemaPopupInfoDTO";

@Component({
    selector: "rask-arbetsorder-scheduler-popover-ngx",
    templateUrl: "./arbetsorder-scheduler-popover-ngx.component.html",
    styleUrls: ["./arbetsorder-scheduler-popover-ngx.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NgStyle,
        ArbetsorderInfoIconComponent,
        RouterLink,
        TextmultilineComponent,
        ArbetsorderStatusIkonerComponent,
        NgbPopover,
        DecimalPipe,
        DatePipe
    ]
})
export class ArbetsorderSchedulerPopoverNgxComponent implements OnDestroy {
    arbetsorderId: number;
    arbetsorderStatusIcons: IArbetsorderStatusIcons;
    arbetsorderValsSynkroniseringstatus: string;
    dirtyProduktion: boolean;
    dirtyTransport: boolean;
    hasSkordningNotStarted: boolean;
    hasTransmissionErrorViol3: boolean;
    isSkordningAvslutad: boolean;
    isSkordningPaborjad: boolean;
    maskinlagEvent: IMaskinLagSchedulerEvent;
    @ViewChild(NgbPopover, { static: true }) popover: NgbPopover;
    schemaPopupInfo: SchemaPopupInfoDTO;
    showSpinner$ = new BehaviorSubject<boolean>(false);
    showViol3Spinner$ = new BehaviorSubject<boolean>(false);
    skickadTillViol3: boolean;
    viol3Icon$ = new BehaviorSubject<string>("initial");
    virkeskopIcon$ = new BehaviorSubject<string>("initial");
    restriktionEjKlar?: boolean;
    restriktionHanterad?: boolean;

    private readonly arbetsorderEventStore = inject(ArbetsorderEventStore);
    private readonly arbetsorderVirkesorderService = inject(ArbetsorderVirkesorderService);
    private readonly calendarService = inject(CalendarService);
    private readonly eventService = inject(EventService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly ngZone = inject(NgZone);

    get arbetsorderBeteckning() {
        return this.maskinlagEvent.arbetsorderBeteckning;
    }

    get dateError() {
        return this.maskinlagEvent.tags.dateError;
    }

    get isGrot(): boolean {
        return ObjectUtils.isNullOrUndefined(this.schemaPopupInfo?.isGrot) ? undefined : this.schemaPopupInfo?.isGrot;
    }

    get isValsSynkroniseringsFel() {
        return this.maskinlagEvent.valsSynkroniseringsstatus === ValsSynkroniseringsstatusTyp.Fel;
    }

    get isVirkeskop() {
        const { maskinlagEvent: event } = this;
        return event.arbetsorderAffarsform && Anskaffningsformer.isVirkesKop(event.arbetsorderAnskaffningsform);
    }

    get namn() {
        return this.maskinlagEvent.tags.namn;
    }

    get prestation() {
        const { maskinlagEvent: event } = this;

        const volym = event.volymArbetsorder;
        const antalDagar = DateUtils.countDaysOfType(moment(event.start.toString()), moment(event.end.toString()), date => !this.isWeekendOrHelgdag(date));
        return antalDagar ? volym / antalDagar : volym;
    }

    get skordadVolymPerPlanerad() {
        return !isNil(this.schemaPopupInfo?.skordadVolym) &&
            !isNil(this.schemaPopupInfo?.planeradVolym &&
                this.schemaPopupInfo?.planeradVolym > 0) ?
            Math.round(this.schemaPopupInfo.skordadVolym / this.schemaPopupInfo.planeradVolym * 100)
            : "";
    }

    get virkesOrderStatusIcon() {
        return this.arbetsorderStatusIcons.viol3 ? VirkesorderStatus.VirkesorderStatusIconStyleViol3(
            this.arbetsorderStatusIcons.hasTransmissionErrorProduktionViol3,
            this.arbetsorderStatusIcons.hasTransmissionErrorTransportViol3,
            this.arbetsorderStatusIcons.dirtyProduktionsdel,
            this.arbetsorderStatusIcons.dirtyTransportdel,
            this.arbetsorderStatusIcons.skickadTillViol3) :
            VirkesorderStatus.VirkesorderStatusIconStyle(this.maskinlagEvent.virkesorderStatus);
    }

    getColor(): string {
        return this.schemaPopupInfo?.arbetsorderFarg;
    }

    readonly isWeekendOrHelgdag = (date: string | Date | moment.Moment): boolean => {
        const day = moment(date);
        return day.isoWeekday() === 7 || day.isoWeekday() === 6 || this.calendarService.isHelgdag(day);
    };

    ngOnDestroy(): void {
        this.arbetsorderEventStore.removeArbetsorderList([this.arbetsorderId]);
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    setData(info: SchemaPopupInfoDTO, event: IMaskinLagSchedulerEvent, arbetsorderId: number) {
        this.schemaPopupInfo = info;
        this.maskinlagEvent = event;
        this.arbetsorderId = arbetsorderId;
        this.restriktionEjKlar = info.restriktionEjKlar;
        this.restriktionHanterad = info.restriktionHanterad;
        this.mapToIcons(info);
        this.setSkordningStatus(this.schemaPopupInfo.skordningPaborjadDatum, this.schemaPopupInfo.skordningAvslutadDatum);
        this.setIkonVirkesorderstatus();
        this.setViol3(info);
        this.setupEvents();
        this.setIkonForViol3();
    }

    protected parseDecimal(val?: number, fractions = 0): string {
        return !isNil(val) ? val.toFixed(fractions) : "-";
    }

    protected parseDecimalsConditional(val?: number, applicable = true, fractions = 2): string {
        // eslint-disable-next-line sonarjs/no-selector-parameter
        return applicable ? this.parseDecimal(val, fractions) : "-";
    }

    protected parseDecimalsNotGrot(val?: number, fractions = 2): string {
        return this.parseDecimalsConditional(val, !this.schemaPopupInfo.isGrot, fractions);
    }

    private mapToIcons(popupInfo: SchemaPopupInfoDTO) {
        this.arbetsorderStatusIcons = ArbetsorderStatusIcons.fromPopupInfoFactory(popupInfo);
    }

    private setIkonForViol3(): void {
        const ikon = this.arbetsorderVirkesorderService.getIkonForViol3(
            this.hasTransmissionErrorViol3,
            this.skickadTillViol3,
            this.dirtyProduktion,
            this.dirtyTransport);
        this.viol3Icon$.next(ikon);
        this.triggerDigestCycle();
    }

    private setIkonVirkesorderstatus(): void {
        if (this.schemaPopupInfo.aktuellVirkesorderStatus === VirkesorderStatus.Sand) {
            this.showSpinner$.next(true);
        } else {
            const ikon = this.arbetsorderVirkesorderService.getIkonVirkesorderstatus(
                this.schemaPopupInfo.aktuellVirkesorderStatus,
                this.schemaPopupInfo.voContractDirty,
                this.schemaPopupInfo.skickadTillViol);
            if (!isNil(ikon)) {
                this.virkeskopIcon$.next(ikon);
                this.showSpinner$.next(false);
            }
        }
        this.triggerDigestCycle();
    }

    private setSkordningStatus(skordningPaborjadDatum: Date, skordningAvslutadDatum: Date): void {
        if (skordningAvslutadDatum > skordningPaborjadDatum) {
            this.isSkordningAvslutad = true;
        } else {
            this.isSkordningAvslutad = false;
        }
        this.hasSkordningNotStarted = isNil(skordningPaborjadDatum);
        this.isSkordningPaborjad = !this.isSkordningAvslutad && !isNil(skordningPaborjadDatum);
    }

    private setViol3(popupInfo: SchemaPopupInfoDTO) {
        this.dirtyProduktion = popupInfo.dirtyProduktionsdel;
        this.dirtyTransport = popupInfo.dirtyTransportdel;
        this.hasTransmissionErrorViol3 = popupInfo.hasTransmissionErrorProduktionViol3 || popupInfo.hasTransmissionErrorTransportViol3;
        this.skickadTillViol3 = popupInfo.skickadTillViol3;
        this.setIkonForViol3();
    }

    private setupEvents() {
        this.eventService.subscribeEvent<VirkesorderStatusChangedEventMessage>(VirkesorderStatusChangedEventMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter(event => event.arbetsorderId === this.schemaPopupInfo.arbetsorderId))
            .subscribe((event) => {
                this.schemaPopupInfo.aktuellVirkesorderStatus = event.aktuellVirkesorderStatus;
                this.schemaPopupInfo.skickadTillViol = !ObjectUtils.isNullOrUndefined(event.senastAckadGeneration);
                this.schemaPopupInfo.voContractDirty = event.isDirty;
                this.setIkonVirkesorderstatus();
            });

        this.arbetsorderEventStore.arbetsorderStoreEventsLoaded$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter(x => x.has(this.arbetsorderId)), map(z => z.get(this.arbetsorderId)))
            .subscribe(x => {
                this.dirtyProduktion = x.produktionsdelIsDirty;
                this.dirtyTransport = x.transportdelIsDirty;
                this.hasTransmissionErrorViol3 = x.hasTransmissionErrorViol3;
                this.skickadTillViol3 = ObjectUtils.hasValue(x.senastSkickat);
                this.showViol3Spinner$.next(x.arbetsorderIsSending);
                this.setIkonForViol3();
            });
        this.arbetsorderEventStore.addArbetsorderList([this.arbetsorderId]);

        this.arbetsorderEventStore.arbetsorderSendStatusChanged$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter(x => x.arbetsorderId === this.arbetsorderId))
            .subscribe(x => {
                this.showViol3Spinner$.next(x.arbetsorderIsSending);
                this.triggerDigestCycle();
            });

        this.arbetsorderEventStore.arbetsorderSynchFinished$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter(x => x.arbetsorderId === this.arbetsorderId))
            .subscribe(x => {
                this.dirtyProduktion = x.produktionsdelIsDirty;
                this.dirtyTransport = x.transportdelIsDirty;
                this.hasTransmissionErrorViol3 = x.hasTransmissionErrorViol3;
                this.skickadTillViol3 = ObjectUtils.hasValue(x.senastSkickat);
                this.setIkonForViol3();
            });
    }

    private triggerDigestCycle(): void {
        this.ngZone.run(() => {
            // Tvinga körning av digest
        });
    }
}
